import moment from 'moment';

import {registerMPElement} from '../../../../../util/register-element';
import {FIRST_EVENT} from '../../../models/constants';
import {BuilderType, ConditionType, PropertyTab} from '../../../models/enums/builder-enums';
import {BehaviorCountType} from '../../../models/enums/expression-enums';

import {displayNameForBehaviorEvent, isCustomEvent} from '../../../util/index';
import {TrackingEvent} from '../../../util/tracking';

import {FilterableClauseComponent} from '../filterable-clause-component';

import template from './index.jade';
import css from './index.styl?inline';

import './frequencies';

export default registerMPElement(
  `cohorts-event-clause`,
  class EventClauseComponent extends FilterableClauseComponent {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'user-id': {type: `number`, description: `ID of user viewing the builder`},
      };
    }

    shouldUpdate(state) {
      return super.shouldUpdate(state) && !!state.stagedCohort.literals[this.literalIdx].behaviors;
    }

    get config() {
      return {
        ...super.config,
        useShadowDom: true,
        css,
        template,
        helpers: {
          ...super.config.helpers,
          getActionLabel: () => `${this.app.getActiveDataGroupLabel()} that`,
          getActionOperatorLabel: () => (this.helpers.isNegated() ? `did not` : `did`),
          getResourceLabel: () => {
            const eventName = this.behavior.getEvent();
            if (isCustomEvent(eventName) && this.state.loading.customEvents) {
              return `Loading...`;
            } else {
              return displayNameForBehaviorEvent(eventName, this.state.customEvents);
            }
          },
          getDateRangeLabel: () => {
            let label = this.dateRangeBehavior.getDateRangeLabel();
            if (this.dateRangeBehavior !== this.behavior && this.dateRangeBehavior.behaviorWindow) {
              // lowercase relative windows for funnel steps
              label = label.toLowerCase();
            }

            return label;
          },
          getFrequencyOperator: () => this.state.stagedCohort.getFrequencyOperator(this.clausePosition),
          isFunnelStep: () => this.literal.isFunnel,
          toggleDateBuilder: () => {
            const builderTypeDate = BuilderType.Date;
            if (this.helpers.isBuilderOpen(builderTypeDate, this.clausePosition)) {
              this.app.closeBuilder(builderTypeDate, this.clausePosition);
            } else {
              this.helpers.startBuilder(builderTypeDate);
            }
          },
          selectedCustomDateRange: (ev) => {
            const range = ev.detail.value;
            let stagedCohort;
            if (range.from) {
              stagedCohort = this.state.stagedCohort.updateBehaviorDateRange(range.from, range.to, this.clausePosition);
            } else {
              stagedCohort = this.state.stagedCohort.updateBehaviorCustomRange(
                range.unit,
                range.value,
                this.clausePosition,
              );
            }
            this.app.track(
              TrackingEvent.SelectCustomDateRange,
              Object.assign({}, stagedCohort.getTrackingDataForClause(this.clausePosition), {
                days: moment(range.to).diff(moment(range.from), `days`),
              }),
            );
            this.updateStagedCohortAndCloseBuilder(BuilderType.Date, stagedCohort);
          },
          selectedFrequencyOperator: (ev) => {
            const frequencyOperator = ev.detail.value;
            const {stagedCohort} = this.state;
            this.app.track(
              TrackingEvent.SelectFrequencyOperator,
              Object.assign({}, stagedCohort.getTrackingDataForClause(this.clausePosition), {
                operator: frequencyOperator,
              }),
            );
            this.updateStagedCohortAndCloseBuilder(
              BuilderType.Frequency,
              stagedCohort.updateFrequencyOperator(frequencyOperator, this.clausePosition),
            );
          },
          sentenceConnector: () => {
            let connector = `from`;
            if (this.dateRangeBehavior.behaviorWindow) {
              connector = this.literal.isFunnel && this.clauseIdx !== 0 ? `within the` : `in the`;
            } else if (this.dateRangeBehavior.fromDate === this.dateRangeBehavior.toDate) {
              connector = `on`;
            } else if (!this.dateRangeBehavior.toDate) {
              connector = `since`;
            }

            if (this.dateRangeBehavior.count.type === BehaviorCountType.Day && !this.helpers.isNegated()) {
              connector = `on unique days, ${connector}`;
            }

            return connector;
          },
          shouldShowFrequencyBuilder: () => !this.helpers.isNegated() && this.behavior.getEvent() !== FIRST_EVENT,
        },
      };
    }

    get conditionType() {
      return ConditionType.Events;
    }

    get defaultPropertyFilterTab() {
      return PropertyTab.Events;
    }

    get dateRangeBehavior() {
      const shouldMirrorFirstClause = this.literal.isFunnel && this.clauseIdx > 0;
      const firstClauseBehavior = this.state.stagedCohort.getBehavior({literalIdx: this.literalIdx, clauseIdx: 0});
      return shouldMirrorFirstClause ? firstClauseBehavior : this.behavior;
    }

    get behavior() {
      return this.state.stagedCohort.getBehavior(this.clausePosition);
    }
  },
);
